import { type Appointment } from '@/models/appointment.ts';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import FilledButton from '@/components/FilledButton.jsx';

import { PractitionerPictureStack } from '@/pages/MyAppointments/practitioner-picture-stack.tsx';

import {
    buildClickablePractitioner,
    formatLongAppointmentDate,
    formatPractitionerSubtitle,
    getLocalizedDoctor,
    getLocalizedLocation,
    titleAfterId,
} from '@/utils/appointment-card-utils.ts';
import { languageMap } from '@/utils/helpers.js';

interface Props {
    appointment: Appointment;
    wide?: boolean;
}

const useStyles = makeStyles((theme: any) => ({
    card: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '1.5rem',
        paddingRight: '3.5rem',
        // Without border-box the padding is added on top of height:100%, so the card grows
        // past its grid row and overlaps the section below.
        boxSizing: 'border-box',
        backgroundColor: theme.palette.common.white,
        border: theme.border.light,
        borderRadius: theme.radius.field,
        [theme.breakpoints.up('sm')]: {
            height: '100%',
        },
        [theme.breakpoints.down('xs')]: {
            paddingRight: '5.5rem',
        },
    },
    cardWide: {
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '2rem',
            height: 'auto',
        },
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        minWidth: 0,
    },
    avatar: {
        position: 'absolute',
        top: '-1rem',
        right: '-1rem',
        [theme.breakpoints.down('xs')]: {
            top: '0.75rem',
            right: '0.75rem',
        },
    },
    title: {
        ...theme.typography.HL5,
        fontWeight: theme.weight.heading,
        color: theme.palette.common.darkGrey,
    },
    practitioner: {
        ...theme.typography.body1,
        color: theme.palette.common.darkGrey,
    },
    date: {
        ...theme.typography.body1,
        fontWeight: theme.weight.heading,
        color: theme.palette.common.darkGrey,
    },
    location: {
        ...theme.typography.body1,
        color: theme.palette.common.darkGrey,
        opacity: 0.7,
    },
    action: {
        paddingTop: '0.5rem',
        alignSelf: 'flex-start',
        [theme.breakpoints.up('sm')]: {
            marginTop: 'auto',
        },
    },
    actionWide: {
        [theme.breakpoints.up('sm')]: {
            marginTop: 0,
            paddingTop: 0,
            alignSelf: 'center',
            flexShrink: 0,
        },
    },
}));

export const AppointmentSummaryCard = ({ appointment, wide = false }: Props) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const history = useHistory();

    const language = languageMap[i18n.language as keyof typeof languageMap];
    const doctor = getLocalizedDoctor(appointment, language);
    const location = getLocalizedLocation(appointment, language);

    const practitioner = buildClickablePractitioner(doctor, (key) => history.push(`/professional/${key}`));
    const subtitle = formatPractitionerSubtitle(doctor);
    const address = location?.address?.[0]?.text;

    return (
        <div className={clsx(classes.card, wide && classes.cardWide)}>
            {practitioner && (
                <div className={classes.avatar}>
                    <PractitionerPictureStack practitioners={[practitioner]} />
                </div>
            )}

            <div className={classes.details}>
                <Typography className={classes.title}>{titleAfterId(appointment.name)}</Typography>
                {subtitle && <Typography className={classes.practitioner}>{subtitle}</Typography>}
                <Typography className={classes.date}>{formatLongAppointmentDate(appointment.start)}</Typography>
                {address && <Typography className={classes.location}>{address}</Typography>}
            </div>

            <div className={clsx(classes.action, wide && classes.actionWide)}>
                <FilledButton
                    text={t('AppointmentDetailsCta')}
                    onPress={() => history.push(`/appointments/${appointment.id}`)}
                />
            </div>
        </div>
    );
};
